/**
 * Requirements Service for AppBouncer SDK
 * Fetches verification requirements from the backend based on API key and user's country
 */

import { ScreenName, VerificationMethod } from '../orchestration';
import { DEFAULT_ENDPOINTS, DEFAULT_TIMEOUTS } from '../config';

// ============================================================
// Types
// ============================================================

export interface RequirementsData {
  /** Verification session ID (UUID7) for tracking this verification flow */
  session_id: string;
  /** List of required verification methods */
  methods: VerificationMethod[];
  /** Regime level (low, medium, strict) */
  regime: string;
  /** ISO 2-letter country code */
  country: string;
  /** Full country name */
  country_name?: string;
  /** Rule name */
  rule_name?: string;
  /** Number of liveness actions to perform (default: 1) */
  liveness_action_count?: number;
}

export interface RequirementsResponse {
  code: number;
  success: boolean;
  data: RequirementsData;
}

export interface RequirementsServiceConfig {
  /** API base URL */
  apiBaseUrl: string;
  /** Requirements check endpoint path (default: /v1/requirements/check) */
  checkEndpoint?: string;
  /** Include credentials in requests (default: false - credentials cause CORS issues with wildcard origin) */
  includeCredentials?: boolean;
  /** Request timeout in ms (default: 10000) */
  timeout?: number;
}

// ============================================================
// Method to Screen Mapping
// ============================================================

/**
 * Map of backend method names to their corresponding SDK screen sequences
 */
const METHOD_SCREEN_MAP: Record<VerificationMethod, ScreenName[]> = {
  liveness: ['face_position', 'liveness_check', 'liveness_complete'],
  age: [], // Age is captured during liveness, displayed on liveness_complete
  ocr: [
    'document_select',
    'document_front',
    'document_front_preview',
    'document_back',
    'document_back_preview',
    'document_processing',
  ],
  // ocr_fallback is handled dynamically based on risk assessment
  // Screens are added conditionally after age estimation
  ocr_fallback: [],
};

// ============================================================
// Requirements Service Class
// ============================================================

export class RequirementsService {
  private config: Required<RequirementsServiceConfig>;
  private cachedRequirements: RequirementsData | null = null;

  constructor(config: RequirementsServiceConfig) {
    this.config = {
      checkEndpoint: DEFAULT_ENDPOINTS.requirements,
      includeCredentials: false, // credentials cause CORS issues with wildcard origin
      timeout: DEFAULT_TIMEOUTS.requirements,
      ...config,
    };
  }

  /**
   * Check verification requirements for the given API key
   * The backend determines the user's country from their IP address
   *
   * @param apiKey - API key for authentication
   * @param userId - Optional external user identifier for tracking
   */
  async checkRequirements(apiKey: string, userId?: string | null): Promise<RequirementsData> {
    let url = `${this.config.apiBaseUrl}${this.config.checkEndpoint}`;

    // Add user_id query parameter if provided
    if (userId) {
      const urlObj = new URL(url);
      urlObj.searchParams.set('user_id', userId);
      url = urlObj.toString();
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: this.config.includeCredentials ? 'include' : 'omit',
        headers: {
          'X-API-Key': apiKey,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new RequirementsError(
          errorData.detail || errorData.message || `Request failed: ${response.status}`,
          response.status
        );
      }

      const result: RequirementsResponse = await response.json();

      if (!result.success) {
        throw new RequirementsError('Requirements check failed', 400);
      }

      // Cache the requirements for later use
      this.cachedRequirements = result.data;

      return result.data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof RequirementsError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new RequirementsError('Request timeout', 408);
      }

      throw new RequirementsError(error instanceof Error ? error.message : 'Unknown error', 500);
    }
  }

  /**
   * Get cached requirements (if available)
   */
  getCachedRequirements(): RequirementsData | null {
    return this.cachedRequirements;
  }

  /**
   * Get the session ID from cached requirements
   * Returns null if requirements haven't been fetched yet
   */
  getSessionId(): string | null {
    return this.cachedRequirements?.session_id ?? null;
  }

  /**
   * Clear cached requirements
   */
  clearCache(): void {
    this.cachedRequirements = null;
  }

  /**
   * Build screen flow from a list of method names
   * Returns the ordered list of screens the user should navigate through
   */
  buildFlowFromMethods(methods: string[]): ScreenName[] {
    const screens: ScreenName[] = ['initial', 'permissions'];

    // Add liveness/age screens if either is required
    // Age is captured during liveness, so we only need liveness screens
    if (methods.includes('liveness') || methods.includes('age')) {
      screens.push(...METHOD_SCREEN_MAP.liveness);
    }

    // Add OCR screens if required (ocr or ocr_fallback)
    // For ocr_fallback, screens are included but may be skipped at runtime
    // based on age gate (ageVerified === false → proceed to document upload)
    if (methods.includes('ocr') || methods.includes('ocr_fallback')) {
      screens.push(...METHOD_SCREEN_MAP.ocr);
    }

    // Add final processing and success screens
    screens.push('processing', 'success');

    return screens;
  }

  /**
   * Check if OCR should be triggered based on age gate result.
   *
   * Simple age gate:
   * - If ageVerified is true → skip OCR (age estimation passed)
   * - If ageVerified is false → trigger OCR (need document verification)
   *
   * @param ageVerified - Whether age verification passed
   * @returns true if OCR should be triggered, false if it can be skipped
   */
  shouldTriggerOcrFallback(ageVerified: boolean): boolean {
    return !ageVerified;
  }

  /**
   * Check if the current requirements include ocr_fallback
   */
  hasOcrFallback(): boolean {
    return this.cachedRequirements?.methods.includes('ocr_fallback') ?? false;
  }

  /**
   * Check if a specific method is required
   */
  isMethodRequired(method: VerificationMethod): boolean {
    return this.cachedRequirements?.methods.includes(method) ?? false;
  }

  /**
   * Get the screens needed for a specific method
   */
  getScreensForMethod(method: VerificationMethod): ScreenName[] {
    return METHOD_SCREEN_MAP[method] || [];
  }
}

// ============================================================
// Error Class
// ============================================================

export class RequirementsError extends Error {
  public readonly statusCode: number;

  constructor(message: string, statusCode: number = 500) {
    super(message);
    this.name = 'RequirementsError';
    this.statusCode = statusCode;
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create a RequirementsService instance
 */
export function createRequirementsService(config: RequirementsServiceConfig): RequirementsService {
  return new RequirementsService(config);
}
